import React, { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Grid } from '@material-ui/core';
import { Button, H1, H2, P, Span } from 'components/Ui';
import Loader from 'components/Loader';
import { titleCase } from 'utils/string';
import { formatCurrency } from 'utils/common';
import { apiCall } from 'utils/swaggerClient';
import AddressSelectDisplay from 'containers/PrimoAccount/ServiceLocations/AddressSelectDisplay';
import {
  selectServiceLocations,
  selectSelectedServiceLocation,
} from 'containers/PrimoAccount/selectors';
import acctStyles from 'containers/PrimoAccount/PastOrders/styles.scss';
import OrderDetailsDrawer from './OrderDetails/Drawer';
import OrderItem from './OrderItem';
import styles from './styles.scss';

const fetchEquipmentTxs = async (serviceLocationId, fromDate, toDate) => {
  fromDate = fromDate || moment().subtract(60, 'days').format('YYYY-MM-DD');
  toDate = toDate || moment().add(14, 'days').format('YYYY-MM-DD');

  const resp = await apiCall({
    operationId: 'ServiceLocationsV2.getEquipmentPurchaseHistory',
    parameters: {
      serviceLocationId,
      fromDate,
      toDate,
    },
  });

  return resp?.equipmentHistory || [];
};

let EQUIPMENT_TXS;

const getOrFetchEquipmentTxs = async (...args) => {
  if (EQUIPMENT_TXS) return EQUIPMENT_TXS;
  EQUIPMENT_TXS = fetchEquipmentTxs(...args);
  return EQUIPMENT_TXS;
};

const Order = (props) => {
  const {
    orderNumber,
    orderDate,
    orderTotalAmount,
    trackingNumber,
    equipmentDetails: orderItems,
    showDetails,
  } = props;

  return (
    <section
      className={`${styles.order} MuiPaper-elevation1`}
      key={orderNumber}
    >
      <header>
        <div>
          <H2>{moment(orderDate).format('MMMM D, YYYY')}</H2>
          <h3>{formatCurrency(orderTotalAmount || 0)}</h3>
        </div>
        <div className={styles.headerRight}>
          <h4>
            <Span>Order #</Span>: {orderNumber}
          </h4>
          <Button
            text="Order Details"
            className={styles.btn}
            onClick={showDetails}
          />
        </div>
      </header>
      {trackingNumber && (
        <div className={styles.status}>
          <P className={styles.desc}>Shipped</P>
          <p>
            <Span>Tracking #</Span>: {trackingNumber}
          </p>
        </div>
      )}
      <ol className={styles.items}>
        {orderItems.map((item) => (
          <li key={item.itemNumber}>
            <OrderItem {...item} />
          </li>
        ))}
      </ol>
    </section>
  );
};

const OrdersHistory = () => {
  const serviceLocations = useSelector(selectServiceLocations());
  const selectedServiceLocation = useSelector(selectSelectedServiceLocation());
  const [serviceLocationId, setServiceLocationId] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [equipmentTxs, setEquipmentTxs] = useState([]);
  const [selectedTx, setSelectedTx] = useState(null);

  const fetchTxs = useCallback(async () => {
    if (!serviceLocationId) return;

    try {
      setIsFetching(true);
      const txs = await getOrFetchEquipmentTxs(serviceLocationId);
      setEquipmentTxs(txs);
    } catch (e) {
      console.error('fetchEquipmentTxs', e); // eslint-disable-line
      setFetchError(e);
    } finally {
      setIsFetching(false);
    }
  }, [serviceLocationId]);

  useEffect(() => {
    fetchTxs();
  }, [fetchTxs]);

  const handleAddressChange = (addressId) => {
    setServiceLocationId(addressId);
  };

  const svcLocsSelectedOnTop = (() => {
    if (!serviceLocations || !selectedServiceLocation) return null;

    const nonMigratedSvcLocs = serviceLocations.filter(
      (loc) => loc.shipToBtbMgnStatus !== 'Migrated',
    );

    const sorted = nonMigratedSvcLocs.sort((a, b) => {
      if (a.id === selectedServiceLocation.id) return -1;
      if (b.id === selectedServiceLocation.id) return 1;
      return 0;
    });

    return sorted;
  })();

  const selectedAddress = (() => {
    const { address1, address2, city = '', stateOrProvinceCode, postalCode } =
      selectedServiceLocation || {};

    const streets = titleCase(address2 ? `${address1}, ${address2}` : address1);

    return `${streets}, ${titleCase(
      city,
    )}, ${stateOrProvinceCode} ${postalCode}`;
  })();

  return (
    <article>
      <header>
        <div className={acctStyles.title}>
          <H1>Recent Dispenser Purchases</H1>
        </div>
        <div className={acctStyles.search}>
          <form>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={12} md={6}>
                <AddressSelectDisplay
                  className={styles.serviceLocations}
                  addresses={svcLocsSelectedOnTop}
                  name="dispenserDeliveryLocation"
                  handleAddressChange={handleAddressChange}
                />
              </Grid>
            </Grid>
          </form>
        </div>
      </header>
      <Loader local loading={isFetching}>
        {!!fetchError && (
          <P className={styles.error}>
            Sorry!
            <br />
            We are unable to load your order history at this time.
            <br />
            Please try again later.
          </P>
        )}
        {!fetchError && !equipmentTxs.length && (
          <P className={styles.empty}>No recent orders.</P>
        )}
        {!!equipmentTxs.length && (
          <>
            {equipmentTxs.map((tx) => (
              <Order
                key={tx.orderNumber}
                {...tx}
                showDetails={() => setSelectedTx(tx)}
              />
            ))}
            <OrderDetailsDrawer
              order={selectedTx}
              address={selectedAddress}
              onClose={() => setSelectedTx(null)}
            />
          </>
        )}
      </Loader>
    </article>
  );
};

export default memo(OrdersHistory);
